import { useGetAiWorkspaceRequest } from "@entities/aiWorkspace";
import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";

const VET_MODULES = new Set([
  "script-generation",
  "script-vet",
  "draft-vet",
  "creator-fit",
]);

const REQUEST_TYPES = new Set([
  ...VET_MODULES,
  "script-generate",
]);

export function moduleIdForRequestType(type: string) {
  if (type === "script-generate") return "script-generation";
  return type;
}

export function aiWorkspaceRequestId(request: { _id?: string; id?: string }) {
  return request._id || request.id || "";
}

export function useAiWorkspaceModuleParams() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestId = searchParams.get("requestId") ?? "";
  const creatorScriptGeneratorJobId = searchParams.get("creatorScriptGeneratorJob") ?? "";
  const moduleParam = searchParams.get("module");
  const { data, isFetching } = useGetAiWorkspaceRequest(requestId);
  const openedRequest = data?.data;

  useEffect(() => {
    if (!requestId || moduleParam || !openedRequest) return;
    if (!REQUEST_TYPES.has(openedRequest.type)) return;
    const next = new URLSearchParams(searchParams);
    next.set("module", moduleIdForRequestType(openedRequest.type));
    setSearchParams(next, { replace: true });
  }, [moduleParam, openedRequest, requestId, searchParams, setSearchParams]);

  const activeTab =
    moduleParam ||
    (requestId && openedRequest && REQUEST_TYPES.has(openedRequest.type)
      ? moduleIdForRequestType(openedRequest.type)
      : "overview");

  const writeParams = (mutate: (next: URLSearchParams) => void) => {
    const next = new URLSearchParams(searchParams);
    mutate(next);
    setSearchParams(next);
  };

  const setTab = (tab: string) => {
    writeParams((next) => {
      if (tab === "overview") next.delete("module");
      else next.set("module", tab);
      next.delete("requestId");
      next.delete("creatorScriptGeneratorJob");
    });
  };

  const openRequest = (id: string, type: string) => {
    writeParams((next) => {
      next.set("module", type);
      next.set("requestId", id);
      next.delete("creatorScriptGeneratorJob");
    });
  };

  const closeRequest = () => {
    writeParams((next) => {
      next.delete("requestId");
      next.delete("creatorScriptGeneratorJob");
    });
  };

  const selectCreatorScriptJob = (jobId: string) => {
    writeParams((next) => {
      if (jobId) next.set("creatorScriptGeneratorJob", jobId);
      else next.delete("creatorScriptGeneratorJob");
    });
  };

  return {
    activeTab,
    requestId,
    creatorScriptGeneratorJobId,
    isOpeningRequest: Boolean(requestId) && isFetching && !moduleParam,
    setTab,
    openRequest,
    closeRequest,
    selectCreatorScriptJob,
  };
}
